import React, { useState, useEffect } from 'react';
import useFadeInOnScroll from './useFadeInOnScroll';

const GITHUB_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
  </svg>
);

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 1024
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

export function isProjectPost(p) {
  return Boolean(p) && p.kind === 'project';
}

// One card, used identically by the homepage Projects grid and the Posts page.
export default function ProjectCard({ project, onClick }) {
  const fadeRef = useFadeInOnScroll();
  const isMobile = useIsMobile();
  const { title, description, content, image, imageMobile, embedUrl, tech, repoUrl, dateLabel, featured, flagLabel, specs } = project;

  const body = description || content || '';
  const techList = Array.isArray(tech) ? tech : [];
  // A featured card leads with its photo and shows the schematic below.
  const showEmbed = Boolean(embedUrl) && !isMobile && !featured;
  const imgSrc = imageMobile || image;
  const hasVisual = showEmbed || Boolean(imgSrc);

  const heading = (
    <div className="project-header">
      <h4>{title}</h4>
      <span className="project-date">{dateLabel || ''}</span>
    </div>
  );

  return (
    <div
      className={`project-card${featured ? ' featured' : ''}`}
      ref={fadeRef}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      {featured && (
        <div className="project-lead">
          <span className="project-flag">{flagLabel || 'Flagship build'}</span>
          {heading}
        </div>
      )}
      {hasVisual && (
        <div className="project-image">
          {showEmbed ? (
            <iframe
              src={embedUrl}
              title={title}
              style={{ border: 'none', width: '100%', height: '100%' }}
              allowFullScreen
            />
          ) : (
            <img src={imgSrc} alt={title} />
          )}
        </div>
      )}
      <div className="project-content">
        {!featured && heading}
        {Array.isArray(specs) && specs.length > 0 && (
          <ul className="spec-grid">
            {specs.map(sp => (
              <li key={sp.label}><b>{sp.label}</b><span>{sp.value}</span></li>
            ))}
          </ul>
        )}
        <div className="project-description" dangerouslySetInnerHTML={{ __html: body }} />
        {featured && embedUrl && !isMobile && (
          <div className="project-schematic">
            <span className="schematic-label">Interactive schematic, pan and zoom</span>
            <iframe src={embedUrl} title={`${title} schematic`} loading="lazy" />
          </div>
        )}
        {techList.length > 0 && (
          <div className="project-tech">
            {techList.map(t => <span className="tech-tag" key={t}>{t}</span>)}
          </div>
        )}
        {repoUrl && (
          <div className="project-links">
            <a
              href={repoUrl}
              className="project-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              {GITHUB_ICON}
              Repo Link
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// Shown on the homepage until project posts are created and pinned via /write.
export const DEFAULT_PROJECTS = [
  {
    id: 'default-ereader',
    kind: 'project',
    title: 'Open-Source E-Reader',
    embedUrl: 'https://kicanvas.org/?github=https://github.com/robertengineerr/ereader/blob/main/hardware/e_reader_v0.1/e_reader_v0.1.kicad_sch',
    repoUrl: 'https://github.com/robertengineerr/ereader',
    featured: true,
    flagLabel: 'Flagship build',
    tech: ['C++', 'ESP32-S3', 'Bare-metal', 'E-ink', 'SPI', 'KiCad', 'PlatformIO'],
    specs: [
      { label: 'MCU', value: "ESP32-S3, 16MB flash / 8MB PSRAM" },
      { label: 'Display', value: "Waveshare 7.5\" e-ink, 800x480" },
      { label: 'Storage', value: "MicroSD over SPI, FAT32" },
      { label: 'Input', value: "7x tactile, interrupt driven" },
      { label: 'Power', value: "3.7V LiPo, MCP73831 + AP2112" },
      { label: 'Toolchain', value: "PlatformIO, GxEPD2" },
    ],
    image: '/images/ereader-bringup.jpg',
    dateLabel: 'v0.1 on breadboard',
    description:
      '<p>An open-source e-reader I took from blank schematic to working firmware. Bare-metal <b>C++</b> on an ' +
      '<b>ESP32-S3</b>, no RTOS, just an interrupt-driven state machine moving between library, reading, and settings.</p>' +
      '<p>The display and the SD card sit on <b>separate SPI buses</b> (FSPI and HSPI) so e-ink refreshes never ' +
      'contend with file reads. The seven tactile buttons run off pin-change interrupts rather than polling a loop. ' +
      'Books upload over WiFi to <code>ereader.local</code>, with EPUB, PDF, DOCX and FB2 parsed in the browser so ' +
      'the microcontroller only ever deals with plain text.</p>' +
      '<p><b>Where it stands:</b> firmware runs on breadboard and the v0.1 schematic is drawn. PCB fabrication, the ' +
      'LiPo charging circuit and an enclosure are still ahead. Everything is MIT licensed.</p>',
  },
  {
    id: 'default-guitar-pedal',
    kind: 'project',
    title: 'Guitar Pedal PCB Design',
    embedUrl: 'https://kicanvas.org/?github=https%3A%2F%2Fgithub.com%2Frobertengineerr%2Flinear_power_booster',
    imageMobile: '/images/GuitarPedalPCB.png',
    repoUrl: 'https://github.com/robertengineerr/linear_power_booster/tree/main',
    tech: ['KiCad', 'PCB Design', 'Electronics'],
    description:
      'As a fun side project, I designed a linear power booster pedal for electric guitars using <b>KiCad</b>. ' +
      'The schematic contains a BJT power amplifier, a voltage divider, and a potentiometer for volume control. ' +
      'The PCB design includes proper grounding, component placement, and routing for optimal performance.',
  },
  {
    id: 'default-flow-battery',
    kind: 'project',
    title: 'Flow Battery Control System',
    image: '/images/control_system_logic.png',
    repoUrl: 'https://github.com/robertengineerr/Flow_Battery_Controls/tree/main',
    tech: ['C++', 'Arduino', 'Electronics'],
    description:
      "For my senior design project I worked with engineers from <b>Honeywell</b> and <b>Duke Energy</b> on a SCADA control and monitoring system for solar cell batteries. " +
      "I designed the control algorithms for the embedded controller, built on an <b>Arduino</b> with supporting power and sensing hardware for the prototype.",
  },
];
